import routes from '@/routes';
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Detects user inactivity shared across all browser tabs
 * @param {number} timeout seconds
 * @returns {boolean} isInactive
 */
function useInactivity(timeout = 3) {
  const location = useLocation();
  const [isInactive, setIsInactive] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const { pathname } = location;
    const INACTIVITY_KEY = 'lastActivityTime';

    /**
     * Update last activity time in localStorage
     */
    const updateActivity = () => {
      localStorage.setItem(INACTIVITY_KEY, String(Date.now()));
      setIsInactive(false);
    };

    /**
     * Check if user inactive based on timestamp
     */
    const checkInactivity = () => {
      const lastActivity = parseInt(localStorage.getItem(INACTIVITY_KEY) || '0', 10);
      const elapsed = (Date.now() - lastActivity) / 1000;
      if (elapsed >= timeout) {
        setIsInactive(true);
      } else {
        setIsInactive(false);
      }
    };

    /**
     * Start the inactivity watcher
     */
    const startWatcher = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(checkInactivity, 1000);
    };

    const events = ['mousemove', 'keydown', 'wheel', 'touchstart'];

    if (![routes.login.path].includes(pathname)) {
      // record activity
      events.forEach((event) => window.addEventListener(event, updateActivity));
      // initialize on mount
      updateActivity();
      startWatcher();
    }

    // also listen to changes from other tabs
    const handleStorage = (e) => {
      if (e.key === INACTIVITY_KEY) {
        checkInactivity();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      events.forEach((event) => window.removeEventListener(event, updateActivity));
      window.removeEventListener('storage', handleStorage);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeout, location]);

  return isInactive;
}

export default useInactivity;
